import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {NavLink} from 'react-router-dom'

import ExitToAppSharpIcon from '@mui/icons-material/ExitToAppSharp'
import HelpSharpIcon from '@mui/icons-material/HelpSharp'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import PermIdentitySharpIcon from '@mui/icons-material/PermIdentitySharp'
import SwapHorizSharpIcon from '@mui/icons-material/SwapHorizSharp'
import {Box, Divider, Link, Theme} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import MobileLanguageSelector from '@progyny/components/Navigation/MobileLanguageSelector'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

type MobileNavDropdownProps = {
  isOpen: boolean
  onClose: () => void
  languageName?: string
  languageLoading: boolean
  onOpenLanguagePicker: () => void
  onOpenJourneyPicker: () => void
}

const MobileNavDropdown = ({
  isOpen,
  onClose,
  languageName,
  languageLoading,
  onOpenLanguagePicker,
  onOpenJourneyPicker,
}: MobileNavDropdownProps) => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const isSsoLogin = useMeStore((s) => s.isSsoLogin)
  const companyHasBudget = useMeStore((s) => s.companyHasBudget)
  const toggleHelpOverlay = useMeStore((s) => s.toggleHelpOverlay)
  const user = useMeStore((s) => s.user)
  const {productLine} = useProductLine()
  const {navbarItems, getPath} = useRoutes(
    companyHasBudget,
    undefined,
    user?.member?.contractType!,
    productLine
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  const handleHelpClick = () => {
    toggleHelpOverlay(true)
    onClose()
  }

  return (
    <>
      <Box className={classes.scrim} onClick={onClose} />
      <Box className={classes.panel}>
        <Box component='ul' className={classes.list}>
          {navbarItems.map((item) => (
            <li key={item.path}>
              <Link
                component={NavLink}
                to={item.path}
                color='textPrimary'
                className={classes.navItem}
                onClick={onClose}
              >
                {item.title}
              </Link>
            </li>
          ))}
        </Box>
        <Divider />
        <Box component='ul' className={classes.list}>
          <MobileLanguageSelector
            languageName={languageName}
            languageLoading={languageLoading}
            onOpenPicker={onOpenLanguagePicker}
          />
          <li>
            <Link
              component='button'
              color='textPrimary'
              className={classes.actionItem}
              onClick={handleHelpClick}
            >
              <HelpSharpIcon className={classes.actionIcon} />
              {t('buttons_help_button_help')}
            </Link>
          </li>
          <li>
            <Link
              component={NavLink}
              to={getPath(RouteEnum.PROFILE)}
              color='textPrimary'
              className={classes.actionItem}
              onClick={onClose}
            >
              <PermIdentitySharpIcon className={classes.actionIcon} />
              {t('user_profile')}
            </Link>
          </li>
          {user?.member?.previouslySelectedJourneys?.length! > 0 && (
            <li>
              <Link
                component='button'
                color='textPrimary'
                className={classes.actionItem}
                onClick={onOpenJourneyPicker}
                data-testid='mobile-navigation-manage-journeys'
              >
                <SwapHorizSharpIcon className={classes.actionIcon} />
                <span className={classes.label}>
                  {t('navigation_switch_product_explore_my_journeys')}
                </span>
                <KeyboardArrowDownOutlinedIcon className={classes.chevron} />
              </Link>
            </li>
          )}
          {!isSsoLogin && (
            <li>
              <Link
                component={NavLink}
                to={getPath(RouteEnum.LOGOUT)}
                color='textPrimary'
                className={classes.actionItem}
                onClick={onClose}
              >
                <ExitToAppSharpIcon className={classes.actionIcon} />
                {t('user_logout')}
              </Link>
            </li>
          )}
        </Box>
      </Box>
    </>
  )
}

export default MobileNavDropdown

const useStyles = makeStyles()((theme: Theme) => ({
  scrim: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 149,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  panel: {
    position: 'fixed',
    // Mirrors Navigation's `root` class: appHeaderHeight tall, offset by its 16px top margin.
    top: `calc(${theme.palette.other?.constValues?.appHeaderHeight || 64}px + 26px)`,
    left: 0,
    right: 0,
    zIndex: 150,
    backgroundColor: theme.palette.background.paper,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: `${theme.spacing(1)} 0`,
    '& li': {
      margin: 0,
      padding: 0,
    },
  },
  navItem: {
    display: 'block',
    width: '100%',
    minHeight: 44,
    padding: theme.spacing(2),
    fontSize: 16,
    textDecoration: 'none',
    color: theme.palette.text.primary,
    boxSizing: 'border-box',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    minHeight: 44,
    padding: theme.spacing(2),
    fontSize: 16,
    textAlign: 'left',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    boxSizing: 'border-box',
  },
  actionIcon: {
    fontSize: 20,
    color: theme.palette.text.primary,
  },
  label: {
    flexGrow: 1,
  },
  chevron: {
    fontSize: 20,
    color: theme.palette.text.primary,
  },
}))
